'use client'
import {
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/16/solid'
import { useEffect, useRef, useState, useCallback } from 'react'
import { debounce } from 'lodash'
import { cn } from '../../helpers/classnames'
import { JosefCheckbox } from '../checkbox/josef-checkbox'

export interface JosefMultiSelectWithSearchOption {
  value: string
  label: string
  description?: string
}

export interface JosefMultiSelectWithSearchProps {
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  maxHeight?: string
  // Function to fetch options with pagination and search
  fetchOptions: (params: {
    page: number
    search?: string
  }) => Promise<{
    items: JosefMultiSelectWithSearchOption[]
    hasMore: boolean
    total?: number
  }>
  // Optional: Pre-fetched selected items for display
  selectedItems?: JosefMultiSelectWithSearchOption[]
}

export function JosefMultiSelectWithSearch({
  selected,
  onChange,
  placeholder = 'Select options...',
  className = '',
  disabled = false,
  maxHeight = '300px',
  fetchOptions,
  selectedItems: initialSelectedItems = [],
}: JosefMultiSelectWithSearchProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [options, setOptions] = useState<JosefMultiSelectWithSearchOption[]>([])
  const [selectedItemsMap, setSelectedItemsMap] = useState<Map<string, JosefMultiSelectWithSearchOption>>(new Map())
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState<number | undefined>()

  // Initialize selected items map with pre-fetched items
  useEffect(() => {
    const newMap = new Map()
    initialSelectedItems.forEach((item) => {
      newMap.set(item.value, item)
    })
    setSelectedItemsMap(prevMap => {
      // Merge with existing map to preserve any items already fetched
      const mergedMap = new Map(prevMap)
      newMap.forEach((value, key) => {
        mergedMap.set(key, value)
      })
      return mergedMap
    })
  }, [initialSelectedItems])

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value)
      setPage(1)
      setOptions([])
    }, 500),
    []
  )

  useEffect(() => {
    debouncedSearch(searchTerm)
  }, [searchTerm, debouncedSearch])

  // Fetch data
  const loadOptions = useCallback(async () => {
    if (isLoading) return

    setIsLoading(true)
    try {
      const result = await fetchOptions({
        page,
        search: debouncedSearchTerm || undefined,
      })

      if (page === 1) {
        setOptions(result.items)
      } else {
        setOptions((prev) => [...prev, ...result.items])
      }

      // Update selected items map with new items
      setSelectedItemsMap(prevMap => {
        const newMap = new Map(prevMap)
        result.items.forEach((item) => {
          if (selected.includes(item.value)) {
            newMap.set(item.value, item)
          }
        })
        return newMap
      })

      setHasMore(result.hasMore)
      setTotal(result.total)
    } catch (error) {
      console.error('Error loading options:', error)
    } finally {
      setIsLoading(false)
    }
  }, [page, debouncedSearchTerm, fetchOptions, selected])

  // Load options when dialog opens or search/page changes
  useEffect(() => {
    if (isOpen) {
      loadOptions()
    }
  }, [isOpen, page, debouncedSearchTerm])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || isLoading || !hasMore) return

    const { scrollTop, scrollHeight, clientHeight } = container
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      setPage((prev) => prev + 1)
    }
  }, [isLoading, hasMore])

  // Handle option toggle
  const toggleOption = (option: JosefMultiSelectWithSearchOption) => {
    const newSelected = selected.includes(option.value)
      ? selected.filter((item) => item !== option.value)
      : [...selected, option.value]

    // Update selected items map
    const newMap = new Map(selectedItemsMap)
    if (newSelected.includes(option.value)) {
      newMap.set(option.value, option)
    } else {
      newMap.delete(option.value)
    }
    setSelectedItemsMap(newMap)

    onChange(newSelected)
  }

  // Open/close dialog
  const openDialog = () => {
    if (disabled) return
    setIsOpen(true)
    setPage(1)
    setOptions([])
    setSearchTerm('')
    setDebouncedSearchTerm('')
    dialogRef.current?.showModal()
    setTimeout(() => searchInputRef.current?.focus(), 100)
  }

  const closeDialog = () => {
    setIsOpen(false)
    setSearchTerm('')
    setDebouncedSearchTerm('')
    dialogRef.current?.close()
  }

  // Handle escape key and backdrop click
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => {
      closeDialog()
    }

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        closeDialog()
      }
    }

    dialog.addEventListener('close', handleClose)
    dialog.addEventListener('click', handleBackdropClick)

    return () => {
      dialog.removeEventListener('close', handleClose)
      dialog.removeEventListener('click', handleBackdropClick)
    }
  }, [])

  // Clear all selections
  const clearAll = () => {
    onChange([])
    setSelectedItemsMap(new Map())
  }

  // Get display text for trigger
  const getDisplayText = () => {
    if (selected.length === 0) return placeholder
    if (selected.length === 1) {
      const item = selectedItemsMap.get(selected[0])
      return item?.label || selected[0]
    }
    return `${selected.length} selected`
  }

  return (
    <div className="space-y-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={openDialog}
        disabled={disabled}
        className={cn(
          'relative w-full min-h-[40px] px-3 py-2 text-left text-sm bg-white dark:bg-black rounded-lg text-black dark:text-white transition-colors',
          'focus:outline-none',
          'hover:bg-black/5 dark:hover:bg-white/5',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <div className="flex items-center justify-between">
          <span
            className={cn(
              'block truncate',
              selected.length === 0 && 'text-gray-500 dark:text-gray-400'
            )}
          >
            {getDisplayText()}
          </span>
          <div className="flex items-center gap-2">
            {selected.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-black/10 dark:bg-white/10 text-black dark:text-white">
                {selected.length}
              </span>
            )}
            <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
      </button>

      {/* Selected Options Display */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((value) => {
            const item = selectedItemsMap.get(value)
            if (!item) return null
            return (
              <div
                key={value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded text-xs text-black dark:text-white"
              >
                <span className="truncate max-w-[120px]">{item.label}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleOption(item)
                  }}
                  className="flex-shrink-0 hover:bg-black/10 dark:hover:bg-white/10 rounded p-0.5 transition-colors"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Native Dialog */}
      <dialog
        ref={dialogRef}
        className={cn(
          'p-0 m-0 border-0 rounded-lg shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm',
          'bg-[#FAFAFA] dark:bg-[#171717] border border-black/10 dark:border-white/10',
          'w-full max-w-md'
        )}
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
        }}
      >
        <div className="flex flex-col max-h-[80vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10">
            <h3 className="text-sm font-medium text-black dark:text-white">
              Select Options
            </h3>
            <button
              type="button"
              onClick={closeDialog}
              className={cn(
                'inline-flex items-center justify-center w-8 h-8 rounded-lg',
                'text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white',
                'hover:bg-black/5 dark:hover:bg-white/5 transition-colors'
              )}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-black/10 dark:border-white/10">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-500 dark:text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search options..."
                className={cn(
                  'w-full h-10 pl-10 pr-3 py-2 text-sm bg-white dark:bg-black/50',
                  'border border-black/10 dark:border-white/10 rounded-lg',
                  'text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400',
                  'focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20',
                  'focus:border-black/20 dark:focus:border-white/20 transition-colors'
                )}
              />
            </div>
          </div>

          {/* Status Bar */}
          <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {selected.length} selected{total ? ` of ${total}` : ''}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={clearAll}
                className={cn(
                  'px-3 py-1 text-xs rounded-lg transition-colors',
                  'text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
                )}
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Options List with Infinite Scroll */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto"
            style={{ maxHeight }}
            onScroll={handleScroll}
          >
            {options.length === 0 && !isLoading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                {debouncedSearchTerm ? 'No options found' : 'Start typing to search'}
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {options.map((option) => {
                  const isSelected = selected.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleOption(option)}
                      className={cn(
                        'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left',
                        'hover:bg-black/5 dark:hover:bg-white/5 text-black dark:text-white'
                      )}
                    >
                      <JosefCheckbox
                        checked={isSelected}
                        onCheckedChange={() => toggleOption(option)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {option.label}
                        </div>
                        {option.description && (
                          <div className="text-xs truncate mt-0.5 text-gray-500 dark:text-gray-400">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
                {isLoading && (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                    Loading more...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-black/10 dark:border-white/10">
            <button
              type="button"
              onClick={closeDialog}
              className={cn(
                'w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                'bg-black dark:bg-white text-white dark:text-black',
                'hover:bg-black/90 dark:hover:bg-white/90'
              )}
            >
              Done
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}

export default JosefMultiSelectWithSearch